/* v8 ignore start */
import {Collection} from "@/lib/core/collection";
import {JsonModel} from "@/lib/core/jsonModel";
import {LottieAnimationModel} from "@/lib/lottie/animation/lottieAnimationModel";
import {TLottieAssetJson} from "@/lib/lottie/assets/lottieAssetModelResolver";
import {LottiePrecompositionLayerModel} from "@/lib/lottie/layers/lottiePrecompositionLayerModel";
import {LottieFontModel} from "@/lib/lottie/text/lottieFontModel";
import {memoize} from "@/lib/memoize";
import {AnimatedSegmentModel} from "@/models/animatedSegmentModel";
import {AnimatedSegmentTransitionModel} from "@/models/animatedSegmentTransitionModel";
import {AutoCreateConfigurationModel} from "@/models/autoCreateConfigurationModel";
import {MusicTrackModel} from "@/models/musicTrackModel";

export const TEMPLATE_CREATOR_ID = 2287;

export class AnimatedProjectModel extends JsonModel<AnimatedProjectModel.IJson> {
  public get id(): Maybe<number> {
    return this.json.id;
  }

  public get isTextEnabled(): boolean {
    return this.json.isTextEnabled;
  }

  @memoize
  public get durationInSeconds(): number {
    return this.json.durationInFrames / this.lottieAnimation().frameRate;
  }

  @memoize
  public get segments(): Collection<AnimatedSegmentModel> {
    return new Collection(this.json.segments.map((segmentJson) => new AnimatedSegmentModel(segmentJson)));
  }

  @memoize
  public get transitions(): Collection<AnimatedSegmentTransitionModel> {
    return new Collection(
      this.json.transitions.map((transitionJson) => new AnimatedSegmentTransitionModel(transitionJson))
    );
  }

  @memoize
  public get musicTrack(): MusicTrackModel {
    const musicTrackJson = this.json.musicTrack;

    if (!musicTrackJson) throw new Error("missing music track");

    return new MusicTrackModel(musicTrackJson);
  }

  public lottieAnimation(): LottieAnimationModel {
    let assets: TLottieAssetJson[] = [];
    let layers: LottiePrecompositionLayerModel.IJson[] = [];
    let fontLists: LottieFontModel.IJson[] = [];
    const configuration = AutoCreateConfigurationModel.default();

    const segments = this.segments.toArray();
    const pageById = new Map<string, AnimatedSegmentModel>();

    segments.forEach((page) => pageById.set(page.uuid, page));

    if (this.transitions.length > 0) {
      this.transitions.toArray().forEach((transition) => {
        const newFromPage = pageById.get(transition.fromSegmentUuid);
        const newToPage = pageById.get(transition.toSegmentUuid);

        if (newFromPage && newToPage) {
          pageById.set(newFromPage.uuid, transition.apply(newFromPage));
          pageById.set(newToPage.uuid, transition.apply(newToPage));
        }
      });
    }

    const segmentsWithTransitions = Array.from(pageById.values());

    segmentsWithTransitions.forEach((page) => {
      assets = assets.concat(page.assetObjects);
      layers = layers.concat(page.layerObjects);
      fontLists = fontLists.concat(page.fontList); // TODO: Fonts can be repeated between pages optimize for uniqueness
    });

    return new LottieAnimationModel({
      v: configuration.lottieVersion,
      fr: configuration.frameRate,
      ip: 0,
      op: this.json.durationInFrames,
      w: configuration.width,
      h: configuration.height,
      nm: this.json.name,
      ddd: 0,
      assets: assets.sort((a) => ("p" in a ? -1 : 1)), // Sorting for layers visualizing purposes: "p" denotes an image object
      fonts: {list: fontLists},
      layers,
    });
  }
}

export namespace AnimatedProjectModel {
  export interface IJson {
    id?: number;
    name: string;
    durationInFrames: number;
    viewLink?: string;
    isTextEnabled: boolean;
    distributionStrategy: AutoCreateConfigurationModel.DistributionStrategy;
    creatorId: number;
    musicTrack: MusicTrackModel.IJson;
    transitions: AnimatedSegmentTransitionModel.IJson[];
    segments: AnimatedSegmentModel.IJson[];
    movieInvites?: {id: number; inviteeEmail: string}[];
  }
}
/* v8 ignore stop */
